// src/services/memberProfiles.js
//
// ── Member profile photos ────────────────────────────────────────────────
//
// The photo itself lives in Clerk (user.setProfileImage). This collection is
// only the DISPLAY INDEX that says "this member chose a photo in our app":
//
//   memberProfiles/{userId}  { userId, photoUrl, tagline, updatedAt }
//
// `tagline` (PHOTO-002) is an optional one-liner shown in the member card —
// same doc, so it costs no extra reads. Writes use merge so a photo save never
// clobbers the tagline and vice versa.
//
// Why an index instead of reading Clerk's imageUrl directly: Clerk also fills
// imageUrl from Google/OAuth sign-ins, and the product decision is that only a
// photo the member picked HERE is ever shown. No doc / empty photoUrl ⇒ no photo.
//
// Why look up instead of copying the URL onto comments/milestones/presence:
// removing a photo must remove it from old comments too, and presence uses
// arrayUnion/arrayRemove (exact object equality) — a URL inside that object
// would strand ghost chips if it changed mid-class. So NO existing write path
// changes; surfaces resolve photos here. See adr-member-profile-photos.md.
//
// NEW COLLECTION ⇒ needs a Firestore console rule or every read/write silently
// fails (reference_firestore_rules). Failures here always degrade to "no photo".

import { useEffect, useState } from 'react';
import { collection, doc, documentId, getDocs, query, where } from 'firebase/firestore';
// Writes wait for the member's Firestore identity (adr-firestore-identity.md a2).
import { setDoc } from './firestoreWrite';
import { db, firebaseEnabled } from './firebase';

const COLLECTION = 'memberProfiles';
const TTL_MS = 10 * 60 * 1000;
const CHUNK = 30; // Firestore `in` limit

// userId → { url: string ('' = no photo), tagline: string, at: ms }
const cache = new Map();
// userId → Promise in flight (so ten avatars for one person = one read)
const inflight = new Map();
const listeners = new Set();

function notify() {
    listeners.forEach((fn) => { try { fn(); } catch (e) { /* listener error is not ours */ } });
}

function fresh(userId) {
    const hit = cache.get(userId);
    return hit && Date.now() - hit.at < TTL_MS;
}

// Only Clerk-hosted URLs are ever rendered. The console rule enforces the same
// prefix on write; this is the read-side belt to that brace.
export function isRenderablePhotoUrl(url) {
    return typeof url === 'string' && url.startsWith('https://img.clerk.com/');
}

export const TAGLINE_MAX = 40;

// Single line, trimmed, collapsed whitespace, capped. Used on write AND read so
// a hand-edited doc can never break a layout.
export function cleanTagline(t) {
    if (typeof t !== 'string') return '';
    return t.replace(/\s+/g, ' ').trim().slice(0, TAGLINE_MAX);
}

async function loadChunk(ids) {
    const found = new Map();
    try {
        const snap = await getDocs(query(collection(db, COLLECTION), where(documentId(), 'in', ids)));
        snap.forEach((d) => {
            const data = d.data();
            found.set(d.id, {
                url: isRenderablePhotoUrl(data.photoUrl) ? data.photoUrl : '',
                tagline: cleanTagline(data.tagline),
            });
        });
        const now = Date.now();
        ids.forEach((id) => cache.set(id, { url: '', tagline: '', ...(found.get(id) || {}), at: now }));
    } catch (e) {
        // Rule missing / offline — cache a short-lived miss so we don't hammer.
        const at = Date.now() - TTL_MS + 60 * 1000;
        ids.forEach((id) => { if (!cache.has(id)) cache.set(id, { url: '', tagline: '', at }); });
    }
}

// Resolve photos for a set of userIds. Never throws; resolves when loaded.
export async function loadMemberPhotos(userIds) {
    if (!firebaseEnabled || !Array.isArray(userIds)) return;
    const need = [...new Set(userIds.filter((id) => typeof id === 'string' && id))]
        .filter((id) => !fresh(id) && !inflight.has(id));
    const waits = [...new Set(userIds)].filter((id) => inflight.has(id)).map((id) => inflight.get(id));
    for (let i = 0; i < need.length; i += CHUNK) {
        const ids = need.slice(i, i + CHUNK);
        const p = loadChunk(ids).finally(() => ids.forEach((id) => inflight.delete(id)));
        ids.forEach((id) => inflight.set(id, p));
        waits.push(p);
    }
    if (need.length === 0 && waits.length === 0) return;
    await Promise.all(waits);
    if (need.length) notify();
}

export function getCachedMemberPhoto(userId) {
    return (userId && cache.get(userId)?.url) || '';
}

export function getCachedMemberTagline(userId) {
    return (userId && cache.get(userId)?.tagline) || '';
}

// Hook: returns a stable lookup fn `photoFor(userId) → url | ''`. Re-renders the
// caller when any requested photo resolves or the member changes her own.
export function useMemberPhotos(userIds) {
    const key = Array.isArray(userIds)
        ? [...new Set(userIds.filter(Boolean))].sort().join(',')
        : '';
    const [, setTick] = useState(0);

    useEffect(() => {
        const fn = () => setTick((t) => t + 1);
        listeners.add(fn);
        return () => { listeners.delete(fn); };
    }, []);

    useEffect(() => {
        if (!key) return;
        loadMemberPhotos(key.split(','));
    }, [key]);

    return getCachedMemberPhoto;
}

// Called by Account after a successful Clerk upload (url) or removal ('').
// Primes the cache first so every mounted surface updates immediately, then
// persists. Throws on write failure so Account can surface it.
export async function saveMemberPhoto(userId, url) {
    if (!userId) throw new Error('No user');
    const photoUrl = url && isRenderablePhotoUrl(url) ? url : '';
    if (url && !photoUrl) throw new Error('Unexpected photo host');
    const prev = cache.get(userId);
    cache.set(userId, { tagline: '', ...(prev || {}), url: photoUrl, at: Date.now() });
    notify();
    if (!firebaseEnabled) return;
    try {
        await setDoc(doc(db, COLLECTION, userId), {
            userId,
            photoUrl,
            updatedAt: new Date().toISOString(),
        }, { merge: true });
    } catch (e) {
        if (prev) cache.set(userId, prev); else cache.delete(userId);
        notify();
        throw e;
    }
}

// Tagline save — same prime-then-persist-with-rollback shape as the photo.
// Returns the cleaned value actually stored.
export async function saveMemberTagline(userId, tagline) {
    if (!userId) throw new Error('No user');
    const clean = cleanTagline(tagline);
    const prev = cache.get(userId);
    cache.set(userId, { url: '', ...(prev || {}), tagline: clean, at: Date.now() });
    notify();
    if (!firebaseEnabled) return clean;
    try {
        await setDoc(doc(db, COLLECTION, userId), {
            userId,
            tagline: clean,
            updatedAt: new Date().toISOString(),
        }, { merge: true });
    } catch (e) {
        if (prev) cache.set(userId, prev); else cache.delete(userId);
        notify();
        throw e;
    }
    return clean;
}

// Admin moderation: blank both fields for a member (Danielle's take-down).
export async function clearMemberProfile(userId) {
    if (!userId) throw new Error('No user');
    await setDoc(doc(db, COLLECTION, userId), {
        userId, photoUrl: '', tagline: '', updatedAt: new Date().toISOString(),
    }, { merge: true });
    cache.set(userId, { url: '', tagline: '', at: Date.now() });
    notify();
}

// Account needs a definitive answer for its own profile (bypasses TTL).
export async function fetchOwnMemberProfile(userId) {
    if (!userId) return { url: '', tagline: '' };
    cache.delete(userId);
    await loadMemberPhotos([userId]);
    return { url: getCachedMemberPhoto(userId), tagline: getCachedMemberTagline(userId) };
}

// Account needs a definitive answer for its own avatar (bypasses TTL).
export async function fetchOwnMemberPhoto(userId) {
    if (!userId) return '';
    cache.delete(userId);
    await loadMemberPhotos([userId]);
    return getCachedMemberPhoto(userId);
}
